import type { LibraryFolder } from '../../api/library'

/**
 * Where in the library you are, worked out from the address and nothing else.
 *
 * There are three places and no fourth, which is what keeps the library from needing a mode:
 *
 *   - the **top**, which holds folders and the way into what has not been sorted;
 *   - a **folder**, which holds subfolders and documents;
 *   - the **trash**, which holds what has been deleted and can still be fetched back. It is not a
 *     place documents live in — what it lists is acts of deleting.
 *
 * **There is deliberately no place for "not sorted yet".** A document that is in no folder simply lies
 * at the top of the library, beside the folders. That state is a state *of the document*, not a second
 * library to visit and empty — so it is seen by where the document lies, and there is nothing to name.
 *
 * The address is the truth: `#/library`, `#/library/<folder>`, `#/library/trash`, and one more step for
 * the document that is open. So a link to a folder is a link somebody can send, and the browser's own
 * back button walks out of it.
 *
 * **A document open at the top of the library needs a step for the place it is in, even though that
 * place is the top.** Without one, `#/library/<id>` would be either a folder or a document and there is
 * no telling which — so [NO_FOLDER] stands in that position: `#/library/-/<document>`. It is one
 * character in an address and it is the reason nothing has to be guessed when a link is opened.
 */
/**
 * The step an older address used for the documents in no folder.
 *
 * There is no such place any more; those documents lie at the top of the library. It is still read, so
 * that a link somebody bookmarked lands where they meant rather than on a folder that does not exist.
 */
export const UNSORTED = 'unsorted'
export const TRASH = 'trash'

/** Stands for "the top of the library" where a step is needed. Never collides with an id. */
export const NO_FOLDER = '-'

export type Place =
  | { readonly kind: 'top' }
  | { readonly kind: 'folder'; readonly id: string }
  | { readonly kind: 'trash' }

export const TOP: Place = { kind: 'top' }

export function placeIn(segment: string | undefined): Place {
  if (segment === undefined || segment === '') {
    return TOP
  }
  if (segment === UNSORTED || segment === NO_FOLDER) {
    return TOP
  }
  return segment === TRASH ? { kind: 'trash' } : { kind: 'folder', id: segment }
}

/** The folder this place is, as the service names it: `null` where it is not a folder at all. */
export function folderOf(place: Place): string | null {
  return place.kind === 'folder' ? place.id : null
}

/**
 * Whether documents of the library live at this place.
 *
 * Everywhere but the trash: at the top lie the documents that are in no folder, and in a folder lie the
 * ones put there. The trash holds things that are no longer in the library at all, and lists them as
 * what was deleted rather than as documents.
 */
export function holdsDocuments(place: Place): boolean {
  return place.kind !== 'trash'
}

/**
 * The address of a place, and of a document open inside it.
 *
 * A document always brings its place with it, the top of the library included — that step is what makes
 * `#/library/-/<document>` a document rather than a folder nobody can find.
 */
export function pathTo(place: Place, document?: string): string[] {
  if (document === undefined) {
    return place.kind === 'top' ? [] : [stepOf(place)]
  }
  return [stepOf(place), document]
}

/**
 * The address of a document to whoever does not know where it lies.
 *
 * A link, a subject or a board knows a kind and an id and deliberately nothing else — that
 * genericity is the whole point of them — so none of them can name the folder. The step for the
 * place is not optional, though: `#/library/<id>` reads as a *folder*, and a folder nobody has ever
 * made shows an empty shelf, which is exactly the wrong answer to "open this document".
 *
 * So the address says "the top", and the library corrects it to where the document actually lies the
 * moment it has asked — which is one request, made only when arriving this way.
 */
export function pathToDocumentWhereverItLies(id: string): string[] {
  return pathTo(TOP, id)
}

/** Where a document lies, as a place. `null` for a folder means the top of the library. */
export function placeOfFolder(folderId: string | null): Place {
  return folderId === null ? TOP : { kind: 'folder', id: folderId }
}

function stepOf(place: Place): string {
  switch (place.kind) {
    case 'folder':
      return place.id
    case 'trash':
      return TRASH
    default:
      return NO_FOLDER
  }
}

/**
 * The folders from the top of the library down to this one, so the way back is visible.
 *
 * Read from the flat list the service sends: the tree is small enough to be answered in one request,
 * which is what lets a breadcrumb be drawn without a request per level. A folder whose parent has
 * meanwhile gone stops the trail rather than looping.
 */
export function trailTo(place: Place, folders: readonly LibraryFolder[]): LibraryFolder[] {
  if (place.kind !== 'folder') {
    return []
  }
  const byId = new Map(folders.map((folder) => [folder.id, folder]))
  const trail: LibraryFolder[] = []
  let next = byId.get(place.id)
  while (next !== undefined && !trail.some((seen) => seen.id === next?.id)) {
    trail.unshift(next)
    next = next.parentId === null ? undefined : byId.get(next.parentId)
  }
  return trail
}

/** What lies directly in this place, from the flat list of all folders. */
export function foldersIn(place: Place, folders: readonly LibraryFolder[]): LibraryFolder[] {
  const parent = place.kind === 'folder' ? place.id : null
  if (place.kind === 'trash') {
    return []
  }
  return folders.filter((folder) => folder.parentId === parent)
}

/**
 * Every folder as a destination, named by its whole path so that two `Klausuren` can be told apart.
 *
 * [without] and everything inside it are left out: a folder cannot be moved into itself or into one of
 * its own subfolders, and offering the impossible and then refusing it is worse than not offering it.
 * The service refuses it as well — this only keeps the list honest.
 */
export function destinationsAmong(
  folders: readonly LibraryFolder[],
  without?: string,
): { id: string; label: string }[] {
  const closed = without === undefined ? new Set<string>() : withEverythingInside(without, folders)
  return folders
    .filter((folder) => !closed.has(folder.id))
    .map((folder) => ({
      id: folder.id,
      label: trailTo({ kind: 'folder', id: folder.id }, folders)
        .map((step) => step.name)
        .join(' / '),
    }))
    .sort((one, other) => one.label.localeCompare(other.label))
}

function withEverythingInside(folder: string, folders: readonly LibraryFolder[]): Set<string> {
  const closed = new Set([folder])
  let grew = true
  while (grew) {
    grew = false
    for (const candidate of folders) {
      if (
        candidate.parentId !== null &&
        closed.has(candidate.parentId) &&
        !closed.has(candidate.id)
      ) {
        closed.add(candidate.id)
        grew = true
      }
    }
  }
  return closed
}

/** What this place is called, in the bar and in a heading. */
export function nameOf(place: Place, folders: readonly LibraryFolder[]): string {
  if (place.kind === 'top') {
    return 'Library'
  }
  if (place.kind === 'trash') {
    return 'Trash'
  }
  return trailTo(place, folders).at(-1)?.name ?? 'Folder'
}

/**
 * The step that says a document is open with nothing else beside it.
 *
 * In the address rather than in a piece of state, like everywhere else in MyLib: full screen is then
 * something somebody can link to and come back out of with the browser's own back button, and the
 * frame's back control removes it without knowing what it means.
 */
export const WRITING = 'write'

/**
 * How the shelf is arranged. The same content, in three densities.
 *
 * **Tiles are the normal case and the reason the library does not feel like a file list**: a document
 * shows its own first page. The other two exist for the question a wall of pictures cannot answer —
 * which of these four is the newest, which is the big one, what is in here at all — because a tile
 * shows every thing well and no two things next to each other.
 */
export const VIEWS = ['tiles', 'list', 'details'] as const

export type View = (typeof VIEWS)[number]

export const DEFAULT_VIEW: View = 'tiles'

/** What each view is called where it is chosen. Words, never a bare symbol — see the design chapter. */
export const VIEW_NAMES: Readonly<Record<View, string>> = {
  tiles: 'Tiles',
  list: 'List',
  details: 'Details',
}

/**
 * The view an address asks for, read off its **last** step.
 *
 * Last rather than first, and that is the whole reason this is backwards compatible: every address
 * that was ever linked to — `#/library/<folder>`, `#/library/-/<document>` — keeps meaning exactly
 * what it meant, and gains a step only when somebody chooses something other than tiles. A view is
 * one of three known words and a folder is a uuid, so the two can never be mistaken for each other.
 */
export function viewIn(path: readonly string[]): View {
  const last = path.at(-1)
  return VIEWS.find((view) => view === last) ?? DEFAULT_VIEW
}

/** The same address with the view taken off, which is what everything else here reads. */
export function withoutView(path: readonly string[]): string[] {
  const steps = [...path]
  if (VIEWS.some((view) => view === steps.at(-1))) {
    steps.pop()
  }
  return steps
}

/**
 * The same address, arranged the other way.
 *
 * Tiles leave no step behind: the normal case has the short address, so a link somebody sends is the
 * one they would have sent before this existed.
 */
export function withView(path: readonly string[], view: View): string[] {
  const steps = withoutView(path)
  return view === DEFAULT_VIEW ? steps : [...steps, view]
}

/** Whether a step of an address is one of the three view words. Builds nothing; see [Library]. */
export function isViewStep(segment: string | undefined): boolean {
  return VIEWS.some((view) => view === segment)
}
